import { LoadingIndicatorComponent } from '../../components/loading-indicator/loading-indicator.component';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { SankhyaGatewayService } from '../../services/sankhya-gateway.service';
import { SerialService, SerialDisponivel } from '../../services/serial.service';
import { LoteService, LoteDisponivel } from '../../services/lote.service';

interface SeparacaoItem {
  seq: number;
  codprod: number;
  descricao: string;
  refforn?: string;
  condicao?: string;
  local?: string;
  unidade: string;
  qtd: number;
  separado?: boolean;
  // Controle de serial / lote
  tipContest?: string;   // 'E'=Série, 'L'=Lote, 'N'=Sem controle
  serialAtribuido?: string;
  serialFab?: string;
  controle?: string;     // lote atual (TGFITE.CONTROLE)
  estoque?: number;      // quantidade atual no estoque (se o backend enviar)
  // "Item no outro Galpão" -- pendência que sai da fila "A Separar" sem
  // marcar SEPARADO='S' (ver PendenciaSeparacaoComponent/gplseparacao no
  // Aerosys para o equivalente Legado). true enquanto a pendência estiver
  // ABERTA (ninguém buscou ainda).
  pendente?: boolean;
}

interface UsuarioSep {
  codusu: number;
  nomeusu: string;
}

interface SeparadorStatus {
  codusu: number;
  nome: string;
  totalItens: number;
  itensSep: number;
  finalizado: boolean;
  isMe: boolean;
}

@Component({
  selector: 'app-estoque-separacao-itens',
  standalone: true,
  imports: [LoadingIndicatorComponent, CommonModule, FormsModule],
  templateUrl: './estoque-separacao-itens.component.html',
  styleUrls: ['./estoque-separacao-itens.component.css']
})
export class EstoqueSeparacaoItensComponent implements OnInit {
  @Input() nunota!: number;
  @Output() voltar = new EventEmitter<void>();
  @Output() finalizado = new EventEmitter<void>();

  itens: SeparacaoItem[] = [];
  itensFiltrados: SeparacaoItem[] = []; // apenas os itens atribuídos a este usuário (pós-divisão)
  carregando = false;
  erro = '';
  erroSalvar = '';
  clienteNome = '';
  vendedor = '';
  numTag = ''; // Nº da TAG informado ao iniciar a separação

  // Abas
  abaAtiva: 'pendentes' | 'separados' = 'pendentes';

  // Estado global de conclusão (todos os separadores)
  allDone = false;
  finalizando = false;
  erroFinalizar = '';

  // Lista de separadores com progresso individual
  separadores: SeparadorStatus[] = [];
  isSplit = false;

  // Modal Finalizar Separação (abre automaticamente ao marcar o último item)
  modalFinalizar = false;

  // Modal Transferir Separação
  modalTransferir = false;
  buscaTransferir = '';
  usuariosTransferir: UsuarioSep[] = [];
  buscandoTransferir = false;
  selecionadoTransferir: UsuarioSep | null = null;
  transferindo = false;
  erroTransferir = '';
  private _buscaTransferirTimer: any;

  // Modal Dividir Separação
  modalDividir = false;
  buscaUsuario = '';
  usuariosSugeridos: UsuarioSep[] = [];
  selecionados: UsuarioSep[] = [];
  buscandoUsuarios = false;
  dividindo = false;
  erroDividir = '';
  private _buscaTimer: any;

  // Modal Trocar Serial
  modalTrocarSerial = false;
  itemTrocando: SeparacaoItem | null = null;
  serialDisponiveis: SerialDisponivel[] = [];
  buscandoSeriais = false;
  trocandoSerial = false;
  erroTrocar = '';

  // Modal Trocar Lote
  modalTrocarLote = false;
  itemTrocandoLote: SeparacaoItem | null = null;
  loteDisponiveis: LoteDisponivel[] = [];
  buscandoLotes = false;
  trocandoLote = false;
  erroTrocarLote = '';

  // Modal "Item no outro Galpão" -- sinaliza um item como pendente (não
  // separado, mas também não trava a fila -- ver itensPendentes abaixo).
  // Sem identificação de usuário: CODUSU vem da sessão Sankhya logada.
  modalPendencia = false;
  itemPendencia: SeparacaoItem | null = null;
  pendenciaObservacaoRascunho = '';
  marcandoPendente = false;
  erroPendencia = '';
  desfazendoPendenciaSeq: number | null = null;

  constructor(
    private gateway: SankhyaGatewayService,
    private serialService: SerialService,
    private loteService: LoteService
  ) {}

  ngOnInit(): void {
    if (this.nunota) {
      this.carregarItens();
    }
  }

  carregarItens(): void {
    this.carregando = true;
    this.erro = '';
    this.gateway.buscarDetalhesPedidoVenda(this.nunota).subscribe({
      next: (res: any) => {
        const body = res?.responseBody ?? res;
        const det = body?.detalhe ?? body;
        this.clienteNome = det?.cliente ?? '';
        this.vendedor    = det?.vendedor ?? '';

        const lista = det?.itens ?? [];
        this.itens = lista.map((it: any) => ({
          seq:       it.seq ?? 0,
          codprod:   it.codprod ?? 0,
          descricao: it.descricao ?? '',
          refforn:   it.refforn ?? '',
          condicao:  it.condicao ?? '',
          local:     it.local ?? '',
          unidade:   it.codvol ?? 'UN',
          qtd:       Number(it.qtd ?? 0),
          separado:  false,
          tipContest: it.tipContest ?? 'N',
          serialAtribuido: undefined,
          serialFab: undefined,
          controle: it.controle && it.controle.trim() ? it.controle.trim() : undefined,
          estoque: it.estoque != null ? Number(it.estoque) : (it.estoqueAtual != null ? Number(it.estoqueAtual) : undefined)
        }));

        // Busca estado (quais separados + quais atribuídos a este usuário)
        this.gateway.getEstadoItensSeparacao(this.nunota).subscribe({
          next: (estado: any) => {
            if (estado?.sucesso) {
              const separados = new Set<number>(estado.seqsSeparados ?? []);
              const atribuidos = estado.seqsAtribuidos as number[] ?? [];
              this.numTag = estado.numTag ?? '';

              this.itens.forEach(it => { it.separado = separados.has(it.seq); });

              // Se divisão já ocorreu, filtra para mostrar apenas os itens deste usuário
              if (atribuidos.length > 0) {
                const atribSet = new Set<number>(atribuidos);
                this.itensFiltrados = this.itens.filter(it => atribSet.has(it.seq));
              } else {
                this.itensFiltrados = [...this.itens];
              }

              // Verifica se todos os itens (globais) já estão separados
              this.allDone = this.itens.length > 0 &&
                             this.itens.every(it => separados.has(it.seq));

              // Lista de separadores com progresso
              this.isSplit = !!estado.isSplit;
              this.separadores = this.parseSeparadores(estado.separadores);

              // Carrega seriais atribuídos ao pedido
              this.carregarSeriais();
              // Marca itens com pendência "Item no outro Galpão" ABERTA -- saem
              // da fila "A Separar" (ver itensPendentes) sem estar SEPARADO='S'.
              this.carregarPendencias();
            } else {
              // Falha "de verdade" (ex.: separação pertence a outro separador, ou ainda
              // não foi iniciada) — não mostra os itens sem essa checagem confirmada.
              this.itens = [];
              this.itensFiltrados = [];
              this.erro = estado?.erro || 'Não foi possível verificar esta separação.';
            }

            this.carregando = false;
          },
          error: () => {
            this.itensFiltrados = [...this.itens];
            this.carregarSeriais();
            this.carregando = false;
          }
        });
      },
      error: (err: any) => {
        this.erro = err?.error?.erro ?? err?.message ?? 'Erro ao carregar itens.';
        this.carregando = false;
      }
    });
  }

  onVoltar(): void {
    this.voltar.emit();
  }

  toggleItem(item: SeparacaoItem): void {
    // Item pendente ("Item no outro Galpão") só sai desse estado via Desfazer
    // (aqui) ou sendo resolvido na tela global "Itens Pendentes" -- clique no
    // card não marca separado por engano por cima de uma pendência aberta.
    if (item.pendente) return;
    const novoEstado = !item.separado;
    item.separado = novoEstado;
    this.erroSalvar = '';
    this.gateway.salvarEstadoItemSeparacao(this.nunota, item.seq, novoEstado).subscribe({
      next: (res: any) => {
        if (!res?.sucesso) {
          item.separado = !novoEstado;
          this.erroSalvar = res?.erro ?? 'Erro ao salvar item.';
        } else {
          if (res.allDone !== undefined) this.allDone = res.allDone;
          if (res.isSplit    !== undefined) this.isSplit = res.isSplit;
          if (res.separadores) this.separadores = this.parseSeparadores(res.separadores);
          // Se marcou o último item pendente → vai para aba Separados e abre modal
          if (novoEstado && this.itensPendentes.length === 0) {
            this.abaAtiva = 'separados';
            if (this.allDone) {
              this.modalFinalizar = true;
            }
          }
          // Se desmarcou, volta para aba Pendentes
          if (!novoEstado && this.abaAtiva === 'separados') {
            this.abaAtiva = 'pendentes';
          }
        }
      },
      error: (err: any) => {
        item.separado = !novoEstado;
        this.erroSalvar = err?.error?.erro ?? err?.message ?? 'Erro ao salvar item.';
      }
    });
  }

  get itensPendentes(): SeparacaoItem[] {
    return this.itensFiltrados.filter(i => !i.separado && !i.pendente);
  }

  /** Itens efetivamente separados OU sinalizados como pendentes ("Item no outro Galpão") -- ambos saem da fila "A Separar". */
  get itensSeparadosLista(): SeparacaoItem[] {
    return this.itensFiltrados.filter(i => !!i.separado || i.pendente);
  }

  get meusSeparados(): number {
    return this.itensFiltrados.filter(i => !!i.separado).length;
  }

  get progresso(): number {
    if (!this.itensFiltrados.length) return 0;
    return Math.round((this.meusSeparados / this.itensFiltrados.length) * 100);
  }

  /** Todos os MEUS itens estão marcados */
  get meusDone(): boolean {
    return this.itensFiltrados.length > 0 && this.itensFiltrados.every(i => i.separado);
  }

  /** Separadores (que não sou eu) que ainda NÃO terminaram */
  get separadoresAindaEmSep(): SeparadorStatus[] {
    return this.separadores.filter(s => !s.isMe && !s.finalizado);
  }

  /** Separadores (que não sou eu) que JÁ terminaram */
  get separadoresJaFinalizaram(): SeparadorStatus[] {
    return this.separadores.filter(s => !s.isMe && s.finalizado);
  }

  private parseSeparadores(raw: any[]): SeparadorStatus[] {
    if (!Array.isArray(raw)) return [];
    return raw.map(s => ({
      codusu:    Number(s.codusu ?? 0),
      nome:      s.nome ?? '',
      totalItens: Number(s.totalItens ?? 0),
      itensSep:  Number(s.itensSep ?? 0),
      finalizado: !!s.finalizado,
      isMe:      !!s.isMe
    }));
  }

  // ── "Item no outro Galpão" (pendência) ────────────────────────────────────
  // Equivalente nativo do fluxo Legado (ver estoque-separacao-itens-legado
  // no mesmo frontend). Sem tela de identificação: CODUSU/CODUSU_RESOLUCAO
  // são sempre resolvidos no Sankhya via sessão logada.

  /** Busca as pendências ABERTAS globais e marca localmente os itens desta venda que têm uma. */
  private carregarPendencias(): void {
    this.gateway.listarPendenciasSeparacao().subscribe({
      next: (res: any) => {
        if (!res?.sucesso) return;
        const itens: any[] = res.itens ?? [];
        const seqsPendentes = new Set<number>(
          itens.filter(i => Number(i.nunota) === Number(this.nunota)).map(i => Number(i.sequencia))
        );
        this.itens.forEach(it => { it.pendente = seqsPendentes.has(it.seq); });
        // Um item pendente sai da fila "A Separar" -- se essa foi a última
        // pendência/separação faltante, revela a aba Separados (mesmo
        // comportamento de toggleItem/confirmarPendencia).
        if (this.itensPendentes.length === 0 && this.itensFiltrados.length > 0) {
          this.abaAtiva = 'separados';
        }
      },
      error: () => { /* falha silenciosa -- não impede a separação de continuar */ }
    });
  }

  abrirModalPendencia(item: SeparacaoItem, event: Event): void {
    event.stopPropagation();
    this.itemPendencia = item;
    this.pendenciaObservacaoRascunho = '';
    this.erroPendencia = '';
    this.modalPendencia = true;
  }

  fecharModalPendencia(): void {
    if (this.marcandoPendente) return;
    this.modalPendencia = false;
    this.itemPendencia = null;
    this.pendenciaObservacaoRascunho = '';
  }

  confirmarPendencia(): void {
    if (this.marcandoPendente || !this.itemPendencia) return;
    const item = this.itemPendencia;
    this.marcandoPendente = true;
    this.erroPendencia = '';
    const texto = this.pendenciaObservacaoRascunho.trim();
    this.gateway.sinalizarPendenciaSeparacao(this.nunota, item.seq, texto || null).subscribe({
      next: (res: any) => {
        this.marcandoPendente = false;
        if (!res?.sucesso) {
          this.erroPendencia = res?.erro ?? 'Erro ao sinalizar pendência.';
          return;
        }
        item.pendente = true;
        this.modalPendencia = false;
        this.itemPendencia = null;
        this.pendenciaObservacaoRascunho = '';
        if (this.itensPendentes.length === 0) {
          this.abaAtiva = 'separados';
        }
      },
      error: (err: any) => {
        this.marcandoPendente = false;
        this.erroPendencia = err?.error?.erro ?? err?.message ?? 'Erro ao sinalizar pendência.';
      }
    });
  }

  /** Desfaz uma sinalização feita por engano, dentro da mesma sessão de separação. */
  desfazerPendencia(item: SeparacaoItem, event: Event): void {
    event.stopPropagation();
    if (this.desfazendoPendenciaSeq != null) return;
    this.desfazendoPendenciaSeq = item.seq;
    this.gateway.desfazerPendenciaSeparacao(this.nunota, item.seq).subscribe({
      next: (res: any) => {
        this.desfazendoPendenciaSeq = null;
        if (!res?.sucesso) {
          this.erroSalvar = res?.erro ?? 'Erro ao desfazer pendência.';
          return;
        }
        item.pendente = false;
        if (this.abaAtiva === 'separados' && this.itensPendentes.length > 0) {
          this.abaAtiva = 'pendentes';
        }
      },
      error: (err: any) => {
        this.desfazendoPendenciaSeq = null;
        this.erroSalvar = err?.error?.erro ?? err?.message ?? 'Erro ao desfazer pendência.';
      }
    });
  }

  // ── Finalizar Separação ───────────────────────────────────────────────────

  finalizarDoModal(): void {
    this.modalFinalizar = false;
    this.finalizar();
  }

  finalizar(): void {
    if (this.finalizando) return;
    this.finalizando = true;
    this.erroFinalizar = '';
    this.gateway.finalizarSeparacao(this.nunota).subscribe({
      next: (res: any) => {
        this.finalizando = false;
        if (res?.sucesso) {
          this.finalizado.emit();
        } else {
          this.erroFinalizar = res?.erro ?? 'Erro ao finalizar separação.';
        }
      },
      error: (err: any) => {
        this.finalizando = false;
        this.erroFinalizar = err?.error?.erro ?? err?.message ?? 'Falha ao finalizar.';
      }
    });
  }

  // ── Modal Transferir Separação ───────────────────────────────────────────

  abrirModalTransferir(): void {
    this.modalTransferir = true;
    this.buscaTransferir = '';
    this.usuariosTransferir = [];
    this.selecionadoTransferir = null;
    this.erroTransferir = '';
  }

  fecharModalTransferir(): void {
    this.modalTransferir = false;
  }

  onBuscaTransferir(): void {
    clearTimeout(this._buscaTransferirTimer);
    if (this.buscaTransferir.trim().length < 2) {
      this.usuariosTransferir = [];
      return;
    }
    this._buscaTransferirTimer = setTimeout(() => {
      this.buscandoTransferir = true;
      this.gateway.buscarUsuariosSeparacao(this.buscaTransferir).subscribe({
        next: (res: any) => {
          this.buscandoTransferir = false;
          const lista: any[] = res?.lista ?? [];
          this.usuariosTransferir = lista.map(u => ({ codusu: Number(u.codusu), nomeusu: u.nomeusu }));
        },
        error: () => { this.buscandoTransferir = false; }
      });
    }, 300);
  }

  selecionarTransferir(u: UsuarioSep): void {
    this.selecionadoTransferir = u;
    this.buscaTransferir = u.nomeusu;
    this.usuariosTransferir = [];
  }

  confirmarTransferencia(): void {
    if (this.transferindo || !this.selecionadoTransferir) return;
    this.transferindo = true;
    this.erroTransferir = '';
    this.gateway.transferirSeparacao(this.nunota, this.selecionadoTransferir.codusu).subscribe({
      next: (res: any) => {
        this.transferindo = false;
        if (res?.sucesso) {
          this.modalTransferir = false;
          this.voltar.emit(); // não é mais o separador desta nota
        } else {
          this.erroTransferir = res?.erro ?? 'Erro ao transferir separação.';
        }
      },
      error: (err: any) => {
        this.transferindo = false;
        this.erroTransferir = err?.error?.erro ?? err?.message ?? 'Falha ao transferir.';
      }
    });
  }

  // ── Modal Dividir Separação ──────────────────────────────────────────────

  abrirModalDividir(): void {
    this.modalDividir = true;
    this.buscaUsuario = '';
    this.usuariosSugeridos = [];
    this.selecionados = [];
    this.erroDividir = '';
  }

  fecharModalDividir(): void {
    this.modalDividir = false;
  }

  onBuscaUsuario(): void {
    clearTimeout(this._buscaTimer);
    if (this.buscaUsuario.trim().length < 2) {
      this.usuariosSugeridos = [];
      return;
    }
    this._buscaTimer = setTimeout(() => {
      this.buscandoUsuarios = true;
      this.gateway.buscarUsuariosSeparacao(this.buscaUsuario).subscribe({
        next: (res: any) => {
          this.buscandoUsuarios = false;
          const lista: any[] = res?.lista ?? [];
          this.usuariosSugeridos = lista
            .filter(u => !this.selecionados.some(s => s.codusu === u.codusu))
            .map(u => ({ codusu: Number(u.codusu), nomeusu: u.nomeusu }));
        },
        error: () => { this.buscandoUsuarios = false; }
      });
    }, 300);
  }

  adicionarUsuario(u: UsuarioSep): void {
    if (this.selecionados.length >= 2) return; // máx 2 adicionais (3 total)
    if (!this.selecionados.some(s => s.codusu === u.codusu)) {
      this.selecionados.push(u);
    }
    this.buscaUsuario = '';
    this.usuariosSugeridos = [];
  }

  removerUsuario(u: UsuarioSep): void {
    this.selecionados = this.selecionados.filter(s => s.codusu !== u.codusu);
  }

  confirmarDivisao(): void {
    if (this.dividindo || !this.selecionados.length) return;
    this.dividindo = true;
    this.erroDividir = '';
    const ids = this.selecionados.map(s => s.codusu);
    this.gateway.dividirSeparacao(this.nunota, ids).subscribe({
      next: (res: any) => {
        this.dividindo = false;
        if (res?.sucesso) {
          this.modalDividir = false;
          this.carregarItens(); // recarrega com itens filtrados para este usuário
        } else {
          this.erroDividir = res?.erro ?? 'Erro ao dividir separação.';
        }
      },
      error: (err: any) => {
        this.dividindo = false;
        this.erroDividir = err?.error?.erro ?? err?.message ?? 'Falha ao dividir.';
      }
    });
  }

  // ── Controle de Serial ───────────────────────────────────────────────────

  /** Carrega os seriais atribuídos ao pedido e mapeia para os itens correspondentes */
  carregarSeriais(): void {
    const temSerial = this.itens.some(i => i.tipContest === 'E');
    if (!temSerial) return;

    this.serialService.buscarSeriesPorNota(this.nunota).subscribe({
      next: (res: any) => {
        const body = res?.responseBody ?? res;
        const lista: any[] = body?.lista ?? [];
        // Mapeia serial atribuído para cada item por sequencia
        lista.forEach(s => {
          const item = this.itens.find(i => i.seq === Number(s.sequencia));
          if (item) {
            item.serialAtribuido = s.serie ?? '';
            item.serialFab = s.serieFab ?? '';
          }
        });
      },
      error: () => { /* falha silenciosa — serial é informativo */ }
    });
  }

  /** Abre modal de troca de serial para um item */
  abrirModalTrocarSerial(item: SeparacaoItem, event: Event): void {
    event.stopPropagation(); // não dispara toggleItem
    this.itemTrocando = item;
    this.serialDisponiveis = [];
    this.erroTrocar = '';
    this.modalTrocarSerial = true;
    this.buscandoSeriais = true;
    this.serialService.buscarSeriesDisponiveis(item.codprod).subscribe({
      next: (res: any) => {
        const body = res?.responseBody ?? res;
        this.serialDisponiveis = body?.lista ?? [];
        this.buscandoSeriais = false;
      },
      error: () => {
        this.erroTrocar = 'Não foi possível carregar seriais disponíveis.';
        this.buscandoSeriais = false;
      }
    });
  }

  fecharModalTrocarSerial(): void {
    this.modalTrocarSerial = false;
    this.itemTrocando = null;
    this.serialDisponiveis = [];
    this.erroTrocar = '';
    this.trocandoSerial = false;
  }

  /** Troca o serial atribuído: remove o atual e registra o novo */
  async trocarSerial(novoSerial: string): Promise<void> {
    if (this.trocandoSerial || !this.itemTrocando) return;
    const item = this.itemTrocando;
    this.trocandoSerial = true;
    this.erroTrocar = '';

    try {
      // 1. Remove serial atual (se houver)
      if (item.serialAtribuido) {
        const resRem: any = await firstValueFrom(
          this.serialService.removerSerialSaida(this.nunota, item.seq, item.serialAtribuido)
        );
        const bodyRem = resRem?.responseBody ?? resRem;
        if (bodyRem?.sucesso !== true && bodyRem?.sucesso !== 'true') {
          throw new Error(bodyRem?.statusMessage ?? bodyRem?.erro ?? 'Erro ao remover serial anterior.');
        }
      }

      // 2. Registra novo serial
      const resReg: any = await firstValueFrom(
        this.serialService.registrarSerialSaida(this.nunota, item.codprod, item.seq, novoSerial)
      );
      const bodyReg = resReg?.responseBody ?? resReg;
      if (bodyReg?.sucesso !== true && bodyReg?.sucesso !== 'true') {
        // Se o remove já ocorreu, restaura o serial anterior para evitar inconsistência na UI
        if (item.serialAtribuido) {
          this.erroTrocar = (bodyReg?.statusMessage ?? bodyReg?.erro ?? 'Erro ao registrar novo serial.') +
            ' O serial anterior foi mantido no banco — recarregue a página para sincronizar.';
        } else {
          this.erroTrocar = bodyReg?.statusMessage ?? bodyReg?.erro ?? 'Erro ao registrar novo serial.';
        }
        this.trocandoSerial = false;
        return;
      }

      // 3. Atualiza UI somente após confirmação do backend
      item.serialAtribuido = novoSerial;
      this.fecharModalTrocarSerial();
    } catch (err: any) {
      this.erroTrocar = err?.message ?? err?.error?.erro ?? 'Erro ao trocar serial.';
      this.trocandoSerial = false;
    }
  }

  // ── Controle de Lote ─────────────────────────────────────────────────────

  /** Abre modal de troca de lote para um item */
  abrirModalTrocarLote(item: SeparacaoItem, event: Event): void {
    event.stopPropagation();
    this.itemTrocandoLote = item;
    this.loteDisponiveis = [];
    this.erroTrocarLote = '';
    this.modalTrocarLote = true;
    this.buscandoLotes = true;
    this.loteService.buscarLotesDisponiveis(item.codprod, this.nunota).subscribe({
      next: (res: any) => {
        const body = res?.responseBody ?? res;
        this.loteDisponiveis = body?.lista ?? [];
        this.buscandoLotes = false;
      },
      error: () => {
        this.erroTrocarLote = 'Não foi possível carregar forms disponíveis.';
        this.buscandoLotes = false;
      }
    });
  }

  fecharModalTrocarLote(): void {
    this.modalTrocarLote = false;
    this.itemTrocandoLote = null;
    this.loteDisponiveis = [];
    this.erroTrocarLote = '';
    this.trocandoLote = false;
  }

  /** Altera o lote do item — grava CONTROLE e CODLOCALORIG em TGFITE */
  async trocarLote(novoLote: string, codLocal: number): Promise<void> {
    if (this.trocandoLote || !this.itemTrocandoLote) return;
    const item = this.itemTrocandoLote;
    this.trocandoLote = true;
    this.erroTrocarLote = '';

    try {
      const res: any = await firstValueFrom(
        this.loteService.alterarLoteItem(this.nunota, item.seq, novoLote, codLocal)
      );
      const body = res?.responseBody ?? res;
      if (body?.sucesso !== true && body?.sucesso !== 'true') {
        this.erroTrocarLote = body?.statusMessage ?? body?.erro ?? 'Erro ao alterar form.';
        this.trocandoLote = false;
        return;
      }
      // Atualiza UI somente após confirmação do backend
      item.controle = novoLote;
      this.fecharModalTrocarLote();
    } catch (err: any) {
      this.erroTrocarLote = err?.message ?? err?.error?.erro ?? 'Erro ao alterar form.';
      this.trocandoLote = false;
    }
  }
}
